// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

// Base API host: set VITE_API_URL in your frontend env to "https://YOUR-API.vercel.app"
const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");
if (import.meta.env.PROD && !API_BASE) {
  console.warn("VITE_API_URL chybí – požadavky jdou na stejné origin a cookie se ztratí.");
}
// Small helper to prefix /api routes with API_BASE (or keep relative if not set)
function apiUrl(path) {
  if (!path.startsWith("/")) path = "/" + path;
  return API_BASE ? `${API_BASE}${path}` : path; // fallback to same-origin when local
}

// Centralized fetch wrapper with good defaults and error handling
async function apiFetch(
  path,
  { method = "GET", headers = {}, body, cache = "no-store", bust = false } = {}
) {
  const url = apiUrl(path) + (bust ? (path.includes("?") ? "&" : "?") + "t=" + Date.now() : "");
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers,
    cache,
    body,
  });

  // Try JSON first, then text
  let data;
  const ct = r.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    data = await r.json().catch(() => ({}));
  } else {
    const text = await r.text().catch(() => "");
    data = text ? { message: text } : {};
  }

  if (!r.ok) {
    const msg = data?.error || data?.message || `${r.status} ${r.statusText}` || "request_failed";
    const err = new Error(msg);
    err.status = r.status;
    err.payload = data;
    throw err;
  }
  return data;
}

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // ---------------------------
  // Me loader with cache-bust
  // ---------------------------
  const fetchMe = useCallback(async ({ bust } = {}) => {
    try {
      const j = await apiFetch("/api/me", {
        bust,
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      setMe(j);
    } catch {
      setMe({ user: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  async function refreshMe() {
    await fetchMe({ bust: true });
  }

  // ---------------------------------------
  // Email-only signup (Step 1): send code + link
  // ---------------------------------------
  async function signupInit(email) {
    await apiFetch("/api/auth/signup-init", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return true;
  }

  // ------------------------------------------------
  // Verify link (Step 2) — if user clicked in email
  // ------------------------------------------------
  async function verifyEmailToken(token) {
    await apiFetch(`/api/auth/verify?token=${encodeURIComponent(token)}`, {
      method: "GET",
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    await refreshMe();
    return true;
  }

  // ------------------------------------------------
  // Verify 6-digit code (Step 2 alternative)
  // ------------------------------------------------
  async function verifyEmailCode({ email, code }) {
    await apiFetch("/api/auth/verify-code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    });
    await refreshMe();
    return true;
  }

  // ---------------------------------------------------
  // Set password & optional name/username (Step 3)
  // ---------------------------------------------------
  async function setPasswordName({ name, password, username }) {
    await apiFetch("/api/auth/set-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, password, username }),
    });
    await refreshMe();
    return true;
  }

  // ----------------
  // Classic login
  // ----------------
  async function login(email, password) {
    await apiFetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    await refreshMe();
    return true;
  }

  // ----------------
  // Classic logout
  // ----------------
  async function logout() {
    await apiFetch("/api/auth/logout", { method: "POST" });
    setMe({ user: null }); // optimistic reset
    await refreshMe();
  }

  // ------------------------------------------------
  // Google auth helpers
  // ------------------------------------------------
  async function googleLogin(credential) {
    const j = await apiFetch("/api/auth/google", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ credential }),
    });
    await refreshMe();
    return j; // { ok: true, needs_profile: boolean }
  }

  async function linkGoogle(credential) {
    await apiFetch("/api/auth/link/google", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ credential }),
    });
    await refreshMe();
    return true;
  }

  return (
    <Ctx.Provider
      value={{
        me,
        user: me?.user || null,
        credits: me?.credits?.balance ?? null,
        subscription: me?.subscription ?? { status: "free" },
        loading,
        refreshMe,
        // email-only flow
        signupInit,
        verifyEmailToken,
        verifyEmailCode,
        setPasswordName,
        // classic
        login,
        logout,
        // google
        googleLogin,
        linkGoogle,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}
